import * as THREE from "three";
import { AssetLibrary } from "./assets";

/**
 * Sky and clouds behind the hub island (art/interface/README.md drop 2 §8): `sky/sky_day.png` becomes the scene
 * background and three cloud sprites drift across the view far behind the island, wrapping at the edges. The sprites
 * are placed relative to the camera every frame (so the orbiting hub camera never sees them move with the island) and
 * ignore the fog. Presentation only; `dispose` restores the plain sky clear colour.
 */
export class SkyClouds {
  private readonly group = new THREE.Group();
  private readonly sprites: { s: THREE.Sprite; x: number; y: number; speed: number; w: number }[] = [];
  private readonly loader = new THREE.TextureLoader();
  private readonly prevBackground: THREE.Scene["background"];
  // Far behind everything (2026-10-22): at 45 units the sprites stood in front of the war camp's wider ground and a
  // cloud was drawn across the lawn; at 300 the ground hides them, and they show only where the sky does
  static readonly Distance = 300;
  /** Sizes and offsets below are authored at 45 units and scaled out to `Distance`. */
  static readonly K = 300 / 45;
  static readonly Span = 60 * SkyClouds.K;

  constructor(private readonly scene: THREE.Scene, private readonly camera: THREE.Camera) {
    this.prevBackground = scene.background;
    this.loader.load(AssetLibrary.resolveInlined("./interface/sky/sky_day.png"), (t) => { t.colorSpace = THREE.SRGBColorSpace; if (scene.background === this.prevBackground) scene.background = t; });
    // x / y in camera space at Distance (the view is ±12 units tall there): clouds sit in the upper third, over the horizon.
    const specs: [string, number, number, number, number][] = [["cloud_1", -18, 7, 1.3, 14], ["cloud_2", 6, 10, 1.0, 11], ["cloud_3", 20, 5, 1.6, 16]];
    for (const [name, x0, y0, speed0, w0] of specs) {
      const k = SkyClouds.K, x = x0 * k, y = y0 * k, speed = speed0 * k, w = w0 * k;
      const mat = new THREE.SpriteMaterial({ transparent: true, opacity: 0.8, depthWrite: false, fog: false, toneMapped: false });
      this.loader.load(AssetLibrary.resolveInlined(`./interface/sky/${name}.png`), (t) => { t.colorSpace = THREE.SRGBColorSpace; mat.map = t; mat.needsUpdate = true; });
      const s = new THREE.Sprite(mat);
      s.scale.set(w, w * 0.5, 1);
      s.renderOrder = -10;
      this.group.add(s);
      this.sprites.push({ s, x, y, speed, w });
    }
    scene.add(this.group);
  }

  private static readonly fwd = new THREE.Vector3();
  private static readonly right = new THREE.Vector3();
  private static readonly up = new THREE.Vector3();

  update(dt: number): void {
    const cam = this.camera;
    cam.updateMatrixWorld();
    const m = cam.matrixWorld.elements;
    const right = SkyClouds.right.set(m[0], m[1], m[2]).normalize();
    const up = SkyClouds.up.set(m[4], m[5], m[6]).normalize();
    const fwd = SkyClouds.fwd.set(-m[8], -m[9], -m[10]).normalize();
    for (const c of this.sprites) {
      c.x += c.speed * dt;
      if (c.x > SkyClouds.Span / 2 + c.w) c.x = -SkyClouds.Span / 2 - c.w;
      c.s.position.copy(cam.position).addScaledVector(fwd, SkyClouds.Distance).addScaledVector(right, c.x).addScaledVector(up, c.y);
    }
  }

  dispose(): void {
    this.scene.remove(this.group);
    for (const c of this.sprites) { c.s.material.map?.dispose(); c.s.material.dispose(); }
    if (this.scene.background && this.scene.background !== this.prevBackground) (this.scene.background as THREE.Texture).dispose?.();
    this.scene.background = this.prevBackground;
  }
}
